import { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { usePartnerAuth } from '../../context/PartnerAuthContext';
import { usePartnerBooking } from '../../context/PartnerBookingContext';
import { supabasePartner } from '../../lib/supabase';
import { labelBase, borderBase, inputBase } from '../../components/shared/helpers';

// First step of the partner booking flow: choose the AGENT (contact person) for
// this booking. Agents are saved permanently on partner_agents and reused across
// bookings. Tickets route to the selected agent's WhatsApp number, so a phone is
// mandatory. Runs on the isolated partner auth + booking contexts; partner data
// is read/written through the RLS-scoped supabasePartner client only.
export default function PartnerSelectAgent() {
  const { partnerSession, partnerProfile, loadingAuth } = usePartnerAuth();
  const { agentId, setAgentId, fading, go, exitToDashboard } = usePartnerBooking();

  // agents === null → still loading; [] → loaded, none yet.
  const [agents, setAgents] = useState(null);

  // Inline add-agent form.
  const [showAdd, setShowAdd] = useState(false);
  const [newName, setNewName] = useState('');
  const [newPhone, setNewPhone] = useState('');
  const [saving, setSaving] = useState(false);
  const [addError, setAddError] = useState('');
  const [addedNotice, setAddedNotice] = useState(false);

  // Inline edit-agent form. editingId is the id of the agent being edited (only
  // one open at a time), or null when no row is in edit mode.
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editPhone, setEditPhone] = useState('');
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState('');
  const [updatedNotice, setUpdatedNotice] = useState(false);

  const partnerId = partnerProfile?.id ?? null;

  // Load the partner's active agents on mount (oldest first). If they have none,
  // open the add form straight away so the empty state is actionable.
  useEffect(() => {
    if (loadingAuth) return;
    if (!partnerSession || partnerProfile?.approval_status !== 'approved') return;
    if (!partnerId) return;

    let cancelled = false;
    (async () => {
      const { data, error } = await supabasePartner
        .from('partner_agents')
        .select('id, agent_name, agent_phone')
        .eq('partner_id', partnerId)
        .eq('is_active', true)
        .order('created_at', { ascending: true });
      if (cancelled) return;
      const rows = !error && data ? data : [];
      setAgents(rows);
      if (rows.length === 0) setShowAdd(true);
    })();

    return () => { cancelled = true; };
  }, [loadingAuth, partnerSession, partnerProfile, partnerId]);

  // Auto-dismiss the "Agent added" confirmation.
  useEffect(() => {
    if (!addedNotice) return;
    const t = setTimeout(() => setAddedNotice(false), 4000);
    return () => clearTimeout(t);
  }, [addedNotice]);

  // Auto-dismiss the "Agent updated" confirmation.
  useEffect(() => {
    if (!updatedNotice) return;
    const t = setTimeout(() => setUpdatedNotice(false), 4000);
    return () => clearTimeout(t);
  }, [updatedNotice]);

  // Guards — wait for the auth check, then require an approved partner session.
  if (loadingAuth) {
    return (
      <div className="w-full max-w-[480px] p-px rounded-2xl bg-gradient-to-b from-[rgba(212,168,67,0.32)] via-[rgba(255,255,255,0.06)] to-[rgba(255,255,255,0.02)] card-enter">
        <div className="bg-[#0D1E35] rounded-[15px] shadow-[0_40px_80px_rgba(0,0,0,0.55)] px-7 py-8">
          <p className="font-dm-sans text-sm text-white/55 font-light leading-relaxed text-center">
            Loading...
          </p>
        </div>
      </div>
    );
  }
  if (!partnerSession) return <Navigate to="/partner/login" replace />;
  if (partnerProfile?.approval_status !== 'approved') return <Navigate to="/partner" replace />;

  // Phone: digits only, and at least 10 (Indian mobile) since tickets route here.
  const addValid = newName.trim().length > 0 && newPhone.trim().length >= 10;
  const editValid = editName.trim().length > 0 && editPhone.trim().length >= 10;
  const selectedValid = !!agentId && Array.isArray(agents) && agents.some((a) => a.id === agentId);

  // Open the inline edit form for one agent, pre-filled with its current values.
  // Closes any add form or other open edit form (only one form open at a time).
  const openEdit = (agent) => {
    setShowAdd(false);
    setAddError('');
    setEditingId(agent.id);
    setEditName(agent.agent_name || '');
    setEditPhone(agent.agent_phone || '');
    setEditError('');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditName('');
    setEditPhone('');
    setEditError('');
  };

  const handleAdd = async () => {
    if (!addValid || saving) return;
    setSaving(true);
    setAddError('');
    const name = newName.trim();
    const phone = newPhone.trim();
    const { data, error } = await supabasePartner
      .from('partner_agents')
      .insert({ partner_id: partnerId, agent_name: name, agent_phone: phone, is_active: true })
      .select('id, agent_name, agent_phone')
      .single();

    if (error || !data) {
      setAddError('Could not save the agent. Please try again.');
      setSaving(false);
      return;
    }

    // Add to the list, auto-select it, and reset/close the form.
    setAgents((prev) => [...(prev || []), data]);
    setAgentId(data.id);
    setNewName('');
    setNewPhone('');
    setShowAdd(false);
    setAddedNotice(true);
    setSaving(false);
  };

  const handleUpdate = async () => {
    if (!editValid || editSaving || !editingId) return;
    setEditSaving(true);
    setEditError('');
    const name = editName.trim();
    const phone = editPhone.trim();
    const { data, error } = await supabasePartner
      .from('partner_agents')
      .update({ agent_name: name, agent_phone: phone })
      .eq('id', editingId)
      .select('id, agent_name, agent_phone')
      .single();

    if (error || !data) {
      setEditError('Could not update the agent. Please try again.');
      setEditSaving(false);
      return;
    }

    // Replace that agent's values in place. Selection (agentId) is untouched, so
    // the edited agent stays selected if it was, and unselected if it wasn't.
    setAgents((prev) => (prev || []).map((a) => (a.id === data.id ? data : a)));
    setEditingId(null);
    setEditName('');
    setEditPhone('');
    setUpdatedNotice(true);
    setEditSaving(false);
  };

  const loadingAgents = agents === null;

  return (
    <div className={`w-full max-w-[480px] p-px rounded-2xl bg-gradient-to-b from-[rgba(212,168,67,0.32)] via-[rgba(255,255,255,0.06)] to-[rgba(255,255,255,0.02)] card-enter transition-opacity duration-[250ms] ${fading ? 'opacity-0' : 'opacity-100'}`}>
      <div className="bg-[#0D1E35] rounded-[15px] shadow-[0_40px_80px_rgba(0,0,0,0.55)] px-7 py-8">

        {/* Header row: back arrow → dashboard (first step) · Exit to Dashboard */}
        <div className="flex items-center justify-between mb-4">
          <button
            type="button"
            onClick={() => go('/partner')}
            className="flex items-center justify-center w-8 h-8 rounded-full border border-[rgba(212,168,67,0.3)] hover:border-[rgba(212,168,67,0.65)] hover:bg-[rgba(212,168,67,0.06)] transition-[border-color,background-color] duration-200 ease-[cubic-bezier(0.23,1,0.32,1)]"
            aria-label="Go back"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
              <path d="M9 2L4 7L9 12" stroke="#D4A843" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          {/* No hold on this step → exit immediately (release no-ops when no hold) */}
          <button
            type="button"
            onClick={exitToDashboard}
            className="font-dm-sans text-[13px] font-medium text-[rgba(212,168,67,0.6)] hover:text-[#C9A84C] hover:underline underline-offset-2 transition-colors duration-200"
          >
            Exit to Dashboard
          </button>
        </div>

        <p className="font-dm-sans text-[12px] uppercase tracking-[0.18em] text-[rgba(201,168,76,0.85)] mb-2">
          Partner Booking
        </p>
        <h1 className="font-cormorant font-normal text-[1.8rem] leading-[1.1] tracking-[-0.01em] text-white mb-2">
          Select Agent
        </h1>
        <p className="font-dm-sans text-sm text-white/55 font-light mb-4 leading-relaxed">
          Choose the contact person for this booking. Agents are saved for your future bookings.
        </p>

        {/* Ticket-routing notice — styled like the PartnerSelectDate callout */}
        <div className="flex items-start gap-3.5 border border-[rgba(201,168,76,0.4)] rounded-lg bg-[rgba(201,168,76,0.06)] px-4 py-3 mb-5">
          <div className="w-2 h-2 rotate-45 bg-[#D4A843] shrink-0 mt-[3px]" />
          <p className="font-dm-sans text-sm font-medium text-[#D4A843] leading-snug">
            Tickets for this booking will be sent to the selected agent's WhatsApp number.
          </p>
        </div>

        {/* Agent list */}
        {loadingAgents ? (
          <p className="font-dm-sans text-sm text-white/55 font-light text-center py-6 leading-relaxed">
            Loading agents...
          </p>
        ) : agents.length === 0 ? (
          <p className="font-dm-sans text-sm text-white/60 font-light leading-relaxed mb-4">
            Add a contact person to continue.
          </p>
        ) : (
          <div className="flex flex-col gap-1 sm:gap-2 mb-3">
            {agents.map((agent) => (
              <div
                key={agent.id}
                className={`rounded-xl border transition-[border-color,background-color] duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] ${
                  agentId === agent.id
                    ? 'bg-[#162840] border-[#D4A843]'
                    : 'bg-[#162840] border-white/10 hover:border-white/20'
                }`}
              >
                {/* Row: selectable name/phone + check, with a separate Edit control.
                    Select and Edit are sibling buttons (never nested), so clicking
                    Edit can't trigger selection. */}
                <div className="flex items-center">
                  <button
                    type="button"
                    onClick={() => setAgentId(agent.id)}
                    className="flex-1 flex items-center justify-between px-4 py-3 min-w-0 text-left rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgba(201,168,76,0.4)]"
                    aria-pressed={agentId === agent.id}
                  >
                    <div className="flex flex-col gap-0.5 min-w-0">
                      <span className="font-dm-sans text-[15px] font-light text-white truncate">
                        {agent.agent_name}
                      </span>
                      <span className="font-dm-sans text-[13px] font-light text-white/55">
                        {agent.agent_phone}
                      </span>
                    </div>
                    <div className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 ml-3 transition-[background-color,border-color] duration-200 ${
                      agentId === agent.id ? 'bg-[#D4A843]' : 'border border-white/15'
                    }`}>
                      {agentId === agent.id && (
                        <span className="text-[#0A1628] text-[11px] font-bold leading-none">&#10003;</span>
                      )}
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => (editingId === agent.id ? cancelEdit() : openEdit(agent))}
                    className="shrink-0 self-stretch px-3.5 font-dm-sans text-[12px] font-medium text-[#C9A84C] hover:text-[#B8973E] transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgba(201,168,76,0.4)] rounded-r-xl"
                    aria-label={`Edit ${agent.agent_name}`}
                  >
                    {editingId === agent.id ? 'Close' : 'Edit'}
                  </button>
                </div>

                {/* Inline edit form — pre-filled, same fields/validation as add */}
                {editingId === agent.id && (
                  <div className="px-4 pb-4 pt-3 mt-1 border-t border-white/[0.08] flex flex-col gap-4">
                    <div className="group flex flex-col gap-2">
                      <label className={labelBase}>Agent Name</label>
                      <div className={borderBase}>
                        <input
                          type="text"
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          className={inputBase}
                          autoComplete="name"
                          placeholder="Full name"
                        />
                      </div>
                    </div>

                    <div className="group flex flex-col gap-2">
                      <label className={labelBase}>Phone Number</label>
                      <div className={borderBase}>
                        <input
                          type="tel"
                          value={editPhone}
                          onChange={(e) => setEditPhone(e.target.value.replace(/\D/g, ''))}
                          className={inputBase}
                          autoComplete="tel"
                          inputMode="numeric"
                          maxLength={10}
                          placeholder="10-digit WhatsApp number"
                        />
                      </div>
                    </div>

                    {editError && (
                      <p className="font-dm-sans text-[13px] font-light text-[#E5484D] leading-relaxed">
                        {editError}
                      </p>
                    )}

                    <div className="flex gap-3">
                      <button
                        type="button"
                        onClick={handleUpdate}
                        disabled={!editValid || editSaving}
                        className={`flex-1 py-[12px] bg-[#C9A84C] text-[#0A1628] font-dm-sans font-semibold text-[12px] tracking-[0.12em] uppercase rounded-full transition-[transform,opacity,background-color] duration-[160ms] ease-[cubic-bezier(0.23,1,0.32,1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgba(201,168,76,0.5)] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0D1E35] ${
                          editValid && !editSaving ? 'opacity-100 active:scale-[0.97] hover:bg-[#B8973E] cursor-pointer' : 'opacity-30 cursor-not-allowed'
                        }`}
                      >
                        {editSaving ? 'Saving...' : 'Save Changes'}
                      </button>
                      <button
                        type="button"
                        onClick={cancelEdit}
                        disabled={editSaving}
                        className="flex-1 py-[12px] rounded-full border border-white/[0.18] text-white/70 font-dm-sans text-[12px] font-medium tracking-[0.12em] uppercase hover:border-white/30 hover:text-white/90 transition-[border-color,color] duration-200 disabled:opacity-50"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* "Agent added" / "Agent updated" confirmation */}
        {addedNotice && (
          <p className="font-dm-sans text-[13px] font-medium text-[#C9A84C] mb-3" role="status">
            Agent added and selected.
          </p>
        )}
        {updatedNotice && (
          <p className="font-dm-sans text-[13px] font-medium text-[#C9A84C] mb-3" role="status">
            Agent updated.
          </p>
        )}

        {/* Add-agent affordance / inline form. Opening Add closes any open edit. */}
        {!loadingAgents && !showAdd && (
          <button
            type="button"
            onClick={() => { setShowAdd(true); setAddError(''); cancelEdit(); }}
            className="font-dm-sans text-[13px] font-medium text-[#C9A84C] hover:text-[#B8973E] transition-colors duration-200 mb-2"
          >
            + Add agent
          </button>
        )}

        {!loadingAgents && showAdd && (
          <div className="rounded-xl border border-white/[0.1] bg-[#122236] px-4 py-4 mb-2 flex flex-col gap-4">
            <div className="group flex flex-col gap-2">
              <label className={labelBase}>Agent Name</label>
              <div className={borderBase}>
                <input
                  type="text"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  className={inputBase}
                  autoComplete="name"
                  placeholder="Full name"
                />
              </div>
            </div>

            <div className="group flex flex-col gap-2">
              <label className={labelBase}>Phone Number</label>
              <div className={borderBase}>
                <input
                  type="tel"
                  value={newPhone}
                  onChange={(e) => setNewPhone(e.target.value.replace(/\D/g, ''))}
                  className={inputBase}
                  autoComplete="tel"
                  inputMode="numeric"
                  maxLength={10}
                  placeholder="10-digit WhatsApp number"
                />
              </div>
            </div>

            {addError && (
              <p className="font-dm-sans text-[13px] font-light text-[#E5484D] leading-relaxed">
                {addError}
              </p>
            )}

            <div className="flex gap-3">
              <button
                type="button"
                onClick={handleAdd}
                disabled={!addValid || saving}
                className={`flex-1 py-[12px] bg-[#C9A84C] text-[#0A1628] font-dm-sans font-semibold text-[12px] tracking-[0.12em] uppercase rounded-full transition-[transform,opacity,background-color] duration-[160ms] ease-[cubic-bezier(0.23,1,0.32,1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgba(201,168,76,0.5)] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0D1E35] ${
                  addValid && !saving ? 'opacity-100 active:scale-[0.97] hover:bg-[#B8973E] cursor-pointer' : 'opacity-30 cursor-not-allowed'
                }`}
              >
                {saving ? 'Saving...' : 'Save Agent'}
              </button>
              {/* Cancel is only offered when at least one agent already exists —
                  with zero agents the form must stay to allow proceeding. */}
              {agents.length > 0 && (
                <button
                  type="button"
                  onClick={() => { setShowAdd(false); setNewName(''); setNewPhone(''); setAddError(''); }}
                  disabled={saving}
                  className="flex-1 py-[12px] rounded-full border border-white/[0.18] text-white/70 font-dm-sans text-[12px] font-medium tracking-[0.12em] uppercase hover:border-white/30 hover:text-white/90 transition-[border-color,color] duration-200 disabled:opacity-50"
                >
                  Cancel
                </button>
              )}
            </div>
          </div>
        )}

        {/* Continue — disabled until an agent is selected */}
        <button
          type="button"
          onClick={() => selectedValid && go('/partner/book/date')}
          disabled={!selectedValid}
          className={`w-full mt-4 py-[14px] bg-[#C9A84C] text-[#0A1628] font-dm-sans font-medium text-[12.5px] tracking-[0.15em] uppercase rounded-full transition-[transform,opacity] duration-[200ms] ease-[cubic-bezier(0.23,1,0.32,1)] focus-visible:outline-none ${
            selectedValid
              ? 'opacity-100 active:scale-[0.97] hover:bg-[#B8973E] focus-visible:ring-2 focus-visible:ring-[rgba(201,168,76,0.5)] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0D1E35] cursor-pointer'
              : 'opacity-30 cursor-not-allowed'
          }`}
        >
          Continue
        </button>

      </div>
    </div>
  );
}
